import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import type { Category, Currency, HomeData, Product, Profile, Service } from "../api/interface";
import { clearGetCache } from "../api/cache";
import { categoryService, homeService, productService, profileService, resourceService, serviceService } from "../api/services";
import Loading from "../components/Loading";

interface GlobalData {
  profiles: Profile[];
  products: Product[];
  categories: Category[];
  services: Service[];
  countries: string[];
  languages: string[];
  currencies: Currency[];
  home: HomeData | null;
}

interface GlobalDataContextValue extends GlobalData {
  ready: boolean;
  refreshing: boolean;
  error: string;
  refreshAll: () => Promise<void>;
}

const EMPTY_DATA: GlobalData = {
  profiles: [], products: [], categories: [], services: [], countries: [], languages: [], currencies: [], home: null,
};

const GlobalDataContext = createContext<GlobalDataContextValue | undefined>(undefined);

const errorMessage = (error: unknown): string =>
  typeof error === "object" && error !== null && "message" in error && typeof error.message === "string"
    ? error.message
    : "Certaines données n'ont pas pu être chargées.";

export const GlobalDataProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [data, setData] = useState<GlobalData>(EMPTY_DATA);
  const [ready, setReady] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const initialRequestRef = useRef<Promise<void> | null>(null);

  const loadAll = useCallback(async (force = false) => {
    if (force) clearGetCache();
    setRefreshing(ready);
    setError("");

    const requests = await Promise.allSettled([
      profileService.getAll({ page: 1, per_page: 100, status: "published" }),
      productService.getAll({ page: 1, per_page: 100, status: "published" }),
      categoryService.getAll({ page: 1, per_page: 100, status: "active" }),
      serviceService.getAll({ page: 1, per_page: 100, status: "active" }),
      resourceService.getCountries(),
      resourceService.getLanguages(),
      resourceService.getCurrencies(),
      homeService.getHomeData(),
    ]);

    setData((current) => ({
      profiles: requests[0].status === "fulfilled" ? requests[0].value.data ?? [] : current.profiles,
      products: requests[1].status === "fulfilled" ? requests[1].value.data ?? [] : current.products,
      categories: requests[2].status === "fulfilled" ? requests[2].value.data ?? [] : current.categories,
      services: requests[3].status === "fulfilled" ? requests[3].value.data ?? [] : current.services,
      countries: requests[4].status === "fulfilled" ? requests[4].value.data ?? [] : current.countries,
      languages: requests[5].status === "fulfilled" ? requests[5].value.data ?? [] : current.languages,
      currencies: requests[6].status === "fulfilled" ? requests[6].value.data ?? [] : current.currencies,
      home: requests[7].status === "fulfilled" ? requests[7].value.data : current.home,
    }));

    const firstFailure = requests.find((result): result is PromiseRejectedResult => result.status === "rejected");
    if (firstFailure) setError(errorMessage(firstFailure.reason));
    setReady(true);
    setRefreshing(false);
  }, [ready]);

  useEffect(() => {
    // La référence empêche React.StrictMode de lancer deux chargements initiaux.
    if (!initialRequestRef.current) initialRequestRef.current = loadAll(false);
  }, [loadAll]);

  useEffect(() => {
    const refresh = () => void loadAll(true);
    window.addEventListener("oneplacedeal:refresh-data", refresh);
    return () => window.removeEventListener("oneplacedeal:refresh-data", refresh);
  }, [loadAll]);

  const refreshAll = useCallback(async () => loadAll(true), [loadAll]);
  const value = useMemo(() => ({ ...data, ready, refreshing, error, refreshAll }), [data, error, ready, refreshAll, refreshing]);

  if (!ready) return <Loading fullScreen />;
  return <GlobalDataContext.Provider value={value}>{children}</GlobalDataContext.Provider>;
};

export const useGlobalData = (): GlobalDataContextValue => {
  const context = useContext(GlobalDataContext);
  if (!context) throw new Error("useGlobalData doit être utilisé dans GlobalDataProvider.");
  return context;
};
